import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  ArrowRight, CheckCircle, AlertCircle, Loader2, Clock, Shield, Users,
  Star, Zap, MessageSquare, Phone, Eye, DollarSign, Calendar,
  Lock, Award, Sparkles, ChevronDown
} from "lucide-react";
import { fadeUp, Card, GoldButton } from "../components/shared/UIComponents";
import { API } from "../data/constants";

const TEXAS_CITIES = [
  "Dallas", "Houston", "Austin", "Fort Worth", "San Antonio", "Other Texas"
];

const LOAN_TYPES = [
  "Fix & Flip", "Bridge", "DSCR Rental", "Cash-Out Refinance",
  "Construction", "Ground Up", "Multifamily", "Commercial"
];

const DEAL_SIZES = [
  "$100K - $250K", "$250K - $500K", "$500K - $1M", "$1M - $2.5M", "$2.5M+"
];

const DEALS_PER_YEAR = ["1-3", "4-8", "9-15", "16-25", "25+"];

const CAPITAL_RANGES = [
  "$500K - $1M", "$1M - $5M", "$5M - $10M", "$10M - $25M", "$25M+"
];

const inputClass = "block w-full rounded-xl border border-[#e0d8ca] bg-white px-4 py-3 text-sm text-[#0A0A0A] shadow-sm placeholder:text-[#bbb] focus:border-emerald-400 focus:ring-2 focus:ring-emerald-200 transition-all";
const selectClass = inputClass + " appearance-none cursor-pointer";
const labelClass = "block text-xs font-semibold text-[#555] uppercase tracking-wide mb-1.5";

export default function FoundingMembersPage() {
  const [stats, setStats] = useState(null);
  const [borrowerForm, setBorrowerForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", city: "",
    primary_loan_type: "", typical_deal_size: "", deals_per_year: "",
    heard_about_us: "", notes: "",
  });
  const [lenderForm, setLenderForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", company_name: "",
    city: "", state: "TX", capital_available: "", preferred_loan_types: [],
    typical_loan_size: "", heard_about_us: "",
  });
  const [borrowerLoading, setBorrowerLoading] = useState(false);
  const [lenderLoading, setLenderLoading] = useState(false);
  const [borrowerSuccess, setBorrowerSuccess] = useState(false);
  const [lenderSuccess, setLenderSuccess] = useState(false);
  const [borrowerError, setBorrowerError] = useState(null);
  const [lenderError, setLenderError] = useState(null);

  const borrowerFormRef = useRef(null);
  const lenderFormRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/public/founding-member-stats`).then(r => setStats(r.data)).catch(() => {});
  }, []);

  const handleBorrowerSubmit = async (e) => {
    e.preventDefault();
    setBorrowerLoading(true); setBorrowerError(null);
    try {
      await axios.post(`${API}/public/founding-member/borrower`, borrowerForm);
      setBorrowerSuccess(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      setBorrowerError(typeof d === "string" ? d : "Submission failed. Please try again.");
    } finally { setBorrowerLoading(false); }
  };

  const handleLenderSubmit = async (e) => {
    e.preventDefault();
    setLenderLoading(true); setLenderError(null);
    try {
      await axios.post(`${API}/public/founding-member/lender`, lenderForm);
      setLenderSuccess(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      setLenderError(typeof d === "string" ? d : "Submission failed. Please try again.");
    } finally { setLenderLoading(false); }
  };

  const toggleLoanType = (lt) => {
    setLenderForm(p => ({
      ...p,
      preferred_loan_types: p.preferred_loan_types.includes(lt)
        ? p.preferred_loan_types.filter(t => t !== lt)
        : [...p.preferred_loan_types, lt],
    }));
  };

  return (
    <div data-testid="founding-members-page">

      {/* ═══ SECTION 1: HERO ═══ */}
      <section className="relative overflow-hidden bg-[#022c22]" data-testid="founding-hero">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L60 30L30 60L0 30z' fill='none' stroke='%23E5C98B' stroke-width='0.5'/%3E%3C/svg%3E")`,
          backgroundSize: '60px 60px'
        }} />
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-0 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] bg-[#E5C98B]/10 rounded-full blur-[120px]" />
        </div>
        <div className="relative z-10 mx-auto max-w-4xl px-4 py-20 sm:py-28 text-center">
          <motion.p {...fadeUp} className="text-xs uppercase tracking-[0.5em] text-[#E5C98B] font-semibold mb-6" data-testid="founding-label">
            Founding Member Program
          </motion.p>
          <motion.h1 {...fadeUp} className="text-3xl sm:text-5xl md:text-6xl font-semibold tracking-tight leading-[1.1]" data-testid="founding-headline">
            <span className="text-[#E5C98B]">Be Part of Something</span>
            <span className="block mt-2 text-white/90">Built Different</span>
          </motion.h1>
          <motion.p {...fadeUp} className="mt-6 max-w-2xl mx-auto text-base sm:text-lg text-white/70 leading-relaxed">
            Imaani Capital is selecting a small group of founding borrowers and lenders in Texas to shape the platform, get priority access, and receive preferred terms on every deal.
          </motion.p>
          <motion.div {...fadeUp} className="mt-8 flex flex-wrap justify-center gap-4">
            <button onClick={() => borrowerFormRef.current?.scrollIntoView({ behavior: "smooth" })}
              className="px-6 py-3 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 transition-colors shadow-lg shadow-emerald-900/30"
              data-testid="hero-apply-borrower">
              Apply as Founding Borrower
            </button>
            <button onClick={() => lenderFormRef.current?.scrollIntoView({ behavior: "smooth" })}
              className="px-6 py-3 rounded-full border border-white/30 text-white text-sm font-semibold hover:bg-white/10 transition-colors"
              data-testid="hero-apply-lender">
              Apply as Founding Lender
            </button>
          </motion.div>
          <motion.p {...fadeUp} className="mt-6 text-xs text-white/40">
            10 founding borrower spots &mdash; 5 founding lender spots &mdash; Texas only.
          </motion.p>
        </div>
      </section>

      {/* ═══ SECTION 2: COUNTER ═══ */}
      <section className="border-b border-[#e8e0d0] bg-white" data-testid="founding-counter">
        <div className="mx-auto max-w-4xl px-4 py-10 sm:py-14">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <motion.div {...fadeUp} className="rounded-2xl border border-[#e8e0d0] p-6 text-center bg-[#faf7f2]" data-testid="stat-borrower-spots">
              <Users className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
              <div className="text-3xl font-bold text-[#0A0A0A]">{stats?.borrower_spots_remaining ?? "..."}<span className="text-base text-[#888] font-normal">/{stats?.borrower_spots_total ?? 10}</span></div>
              <div className="text-xs text-[#888] mt-1 uppercase tracking-wider font-semibold">Borrower Spots Remaining</div>
            </motion.div>
            <motion.div {...fadeUp} className="rounded-2xl border border-[#e8e0d0] p-6 text-center bg-[#faf7f2]" data-testid="stat-lender-spots">
              <DollarSign className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
              <div className="text-3xl font-bold text-[#0A0A0A]">{stats?.lender_spots_remaining ?? "..."}<span className="text-base text-[#888] font-normal">/{stats?.lender_spots_total ?? 5}</span></div>
              <div className="text-xs text-[#888] mt-1 uppercase tracking-wider font-semibold">Lender Spots Remaining</div>
            </motion.div>
            <motion.div {...fadeUp} className="rounded-2xl border border-[#e8e0d0] p-6 text-center bg-[#faf7f2]" data-testid="stat-launch-date">
              <Calendar className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
              <div className="text-3xl font-bold text-[#0A0A0A]">{stats?.launch_date ?? "March 2026"}</div>
              <div className="text-xs text-[#888] mt-1 uppercase tracking-wider font-semibold">Platform Launch</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 3: BORROWER BENEFITS ═══ */}
      <section className="bg-[#022c22] relative" data-testid="borrower-benefits">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='20' cy='20' r='1' fill='%23E5C98B'/%3E%3C/svg%3E")`,
          backgroundSize: '40px 40px'
        }} />
        <div className="relative z-10 mx-auto max-w-6xl px-4 py-16 sm:py-20">
          <motion.div {...fadeUp} className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.4em] text-[#E5C98B]/70 mb-3">For Borrowers</p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight">Founding Borrower Benefits</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-5">
            {[
              { icon: Zap, title: "Priority Review", desc: "24-hour underwriting guarantee on every deal submission. Your deals are reviewed first, always.", color: "text-emerald-400" },
              { icon: DollarSign, title: "Reduced Origination Fee", desc: "0.5% off the origination fee on your first funded deal through the platform.", color: "text-[#E5C98B]" },
              { icon: Phone, title: "Direct Access", desc: "A direct line to the Imaani Capital team. Skip the queue and get answers when you need them.", color: "text-emerald-400" },
              { icon: MessageSquare, title: "Shape the Product", desc: "Your feedback directly influences platform features. Help us build the tools you actually need.", color: "text-[#E5C98B]" },
            ].map(b => (
              <motion.div {...fadeUp} key={b.title}
                className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 hover:bg-white/[0.08] transition-colors" data-testid={`borrower-benefit-${b.title.toLowerCase().replace(/\s/g, '-')}`}>
                <b.icon className={`h-6 w-6 ${b.color} mb-3`} />
                <h3 className="text-base font-bold text-white mb-1.5">{b.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 4: LENDER BENEFITS ═══ */}
      <section className="bg-[#faf7f2] border-b border-[#e8e0d0]" data-testid="lender-benefits">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:py-20">
          <motion.div {...fadeUp} className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.4em] text-emerald-700/70 mb-3">For Lenders</p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#0A0A0A] tracking-tight">Founding Lender Benefits</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-5">
            {[
              { icon: Eye, title: "First Look", desc: "See deals before the broader lender network. Get first right of refusal on every qualifying opportunity." },
              { icon: Shield, title: "Zero Platform Fee", desc: "No platform fee on your first 3 funded deals. Fund deals with zero overhead from us." },
              { icon: Star, title: "Custom Criteria Setup", desc: "A personal onboarding session to configure your lending criteria. We match deals to your exact box." },
              { icon: Users, title: "Advisory Input", desc: "Quarterly calls with the team to share feedback, discuss market trends, and shape platform direction." },
            ].map(b => (
              <motion.div {...fadeUp} key={b.title}>
                <Card className="h-full" data-testid={`lender-benefit-${b.title.toLowerCase().replace(/\s/g, '-')}`}>
                  <b.icon className="h-6 w-6 text-emerald-600 mb-3" />
                  <h3 className="text-base font-bold text-[#0A0A0A] mb-1.5">{b.title}</h3>
                  <p className="text-sm text-[#888] leading-relaxed">{b.desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 5: HOW IT WORKS ═══ */}
      <section className="bg-white border-b border-[#e8e0d0]" data-testid="how-it-works">
        <div className="mx-auto max-w-4xl px-4 py-16 sm:py-20">
          <motion.div {...fadeUp} className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.4em] text-emerald-700/70 mb-3">Simple Process</p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#0A0A0A] tracking-tight">How It Works</h2>
          </motion.div>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { step: "1", title: "Apply", desc: "Fill out the form below. Takes about 2 minutes.", time: "2 minutes", icon: Sparkles },
              { step: "2", title: "Get Approved", desc: "Our team reviews your application and reaches out.", time: "48 hours", icon: CheckCircle },
              { step: "3", title: "Get Access", desc: "Immediate platform access with all founding member benefits activated.", time: "Immediate", icon: Zap },
            ].map(s => (
              <motion.div {...fadeUp} key={s.step} className="text-center" data-testid={`how-step-${s.step}`}>
                <div className="mx-auto mb-4 h-14 w-14 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                  <s.icon className="h-6 w-6 text-emerald-600" />
                </div>
                <div className="text-[10px] uppercase tracking-wider text-emerald-600 font-bold mb-1">Step {s.step}</div>
                <h3 className="text-lg font-bold text-[#0A0A0A] mb-1">{s.title}</h3>
                <p className="text-sm text-[#888] leading-relaxed">{s.desc}</p>
                <div className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#faf7f2] text-[10px] text-[#888] font-medium">
                  <Clock className="h-3 w-3" /> {s.time}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 6: BORROWER APPLICATION FORM ═══ */}
      <section ref={borrowerFormRef} className="bg-white border-b border-[#e8e0d0]" data-testid="borrower-form-section">
        <div className="mx-auto max-w-2xl px-4 py-16 sm:py-20">
          <motion.div {...fadeUp} className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.4em] text-emerald-700/70 mb-3">Application</p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-[#0A0A0A] tracking-tight">Founding Borrower Application</h2>
            <p className="mt-3 text-sm text-[#888]">Texas real estate investors looking for priority access and preferred terms.</p>
          </motion.div>

          {borrowerSuccess ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="borrower-success">
              <CheckCircle className="h-10 w-10 text-emerald-600 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-emerald-700">Application Submitted</h3>
              <p className="mt-2 text-sm text-emerald-600">We'll review your application within 48 hours and reach out to next steps.</p>
            </motion.div>
          ) : (
            <form onSubmit={handleBorrowerSubmit} className="space-y-5" data-testid="borrower-application-form">
              <div className="grid grid-cols-2 gap-4">
                <label className="block"><span className={labelClass}>First Name *</span>
                  <input required value={borrowerForm.first_name} onChange={e => setBorrowerForm(p => ({ ...p, first_name: e.target.value }))}
                    className={inputClass} placeholder="John" data-testid="borrower-first-name" /></label>
                <label className="block"><span className={labelClass}>Last Name *</span>
                  <input required value={borrowerForm.last_name} onChange={e => setBorrowerForm(p => ({ ...p, last_name: e.target.value }))}
                    className={inputClass} placeholder="Smith" data-testid="borrower-last-name" /></label>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <label className="block"><span className={labelClass}>Email *</span>
                  <input required type="email" value={borrowerForm.email} onChange={e => setBorrowerForm(p => ({ ...p, email: e.target.value }))}
                    className={inputClass} placeholder="john@example.com" data-testid="borrower-email" /></label>
                <label className="block"><span className={labelClass}>Phone *</span>
                  <input required value={borrowerForm.phone} onChange={e => setBorrowerForm(p => ({ ...p, phone: e.target.value }))}
                    className={inputClass} placeholder="(832) 555-1234" data-testid="borrower-phone" /></label>
              </div>
              <label className="block"><span className={labelClass}>City *</span>
                <select required value={borrowerForm.city} onChange={e => setBorrowerForm(p => ({ ...p, city: e.target.value }))}
                  className={selectClass} data-testid="borrower-city">
                  <option value="">Select city</option>
                  {TEXAS_CITIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select></label>
              <label className="block"><span className={labelClass}>Primary Loan Type *</span>
                <select required value={borrowerForm.primary_loan_type} onChange={e => setBorrowerForm(p => ({ ...p, primary_loan_type: e.target.value }))}
                  className={selectClass} data-testid="borrower-loan-type">
                  <option value="">Select loan type</option>
                  {LOAN_TYPES.map(lt => <option key={lt} value={lt}>{lt}</option>)}
                </select></label>
              <div className="grid grid-cols-2 gap-4">
                <label className="block"><span className={labelClass}>Typical Deal Size *</span>
                  <select required value={borrowerForm.typical_deal_size} onChange={e => setBorrowerForm(p => ({ ...p, typical_deal_size: e.target.value }))}
                    className={selectClass} data-testid="borrower-deal-size">
                    <option value="">Select range</option>
                    {DEAL_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select></label>
                <label className="block"><span className={labelClass}>Deals Per Year *</span>
                  <select required value={borrowerForm.deals_per_year} onChange={e => setBorrowerForm(p => ({ ...p, deals_per_year: e.target.value }))}
                    className={selectClass} data-testid="borrower-deals-year">
                    <option value="">Select range</option>
                    {DEALS_PER_YEAR.map(d => <option key={d} value={d}>{d}</option>)}
                  </select></label>
              </div>
              <label className="block"><span className={labelClass}>How did you hear about us?</span>
                <input value={borrowerForm.heard_about_us} onChange={e => setBorrowerForm(p => ({ ...p, heard_about_us: e.target.value }))}
                  className={inputClass} placeholder="Referral, social media, etc." data-testid="borrower-referral" /></label>
              <label className="block"><span className={labelClass}>Notes</span>
                <textarea value={borrowerForm.notes} onChange={e => setBorrowerForm(p => ({ ...p, notes: e.target.value }))} rows={3}
                  className={inputClass + " resize-none"} placeholder="Anything else we should know..." data-testid="borrower-notes" /></label>

              {borrowerError && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-600 flex items-center gap-2" data-testid="borrower-error">
                  <AlertCircle className="h-4 w-4 shrink-0" />{borrowerError}
                </div>
              )}

              <GoldButton type="submit" disabled={borrowerLoading} data-testid="borrower-submit-btn">
                {borrowerLoading ? <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Submitting...</span> : "Submit Borrower Application"}
              </GoldButton>
            </form>
          )}
        </div>
      </section>

      {/* ═══ SECTION 7: LENDER APPLICATION FORM ═══ */}
      <section ref={lenderFormRef} className="bg-[#022c22] relative" data-testid="lender-form-section">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='20' cy='20' r='1' fill='%23E5C98B'/%3E%3C/svg%3E")`,
          backgroundSize: '40px 40px'
        }} />
        <div className="relative z-10 mx-auto max-w-2xl px-4 py-16 sm:py-20">
          <motion.div {...fadeUp} className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.4em] text-[#E5C98B]/70 mb-3">Application</p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight">Founding Lender Application</h2>
            <p className="mt-3 text-sm text-white/60">Private lenders deploying capital in Texas real estate.</p>
          </motion.div>

          {lenderSuccess ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="rounded-2xl border border-emerald-500/30 bg-emerald-900/30 p-8 text-center" data-testid="lender-success">
              <CheckCircle className="h-10 w-10 text-emerald-400 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-emerald-300">Application Submitted</h3>
              <p className="mt-2 text-sm text-emerald-400/80">We'll review your application within 48 hours and schedule your personal onboarding session.</p>
            </motion.div>
          ) : (
            <form onSubmit={handleLenderSubmit} className="space-y-5" data-testid="lender-application-form">
              <div className="grid grid-cols-2 gap-4">
                <label className="block"><span className="block text-xs font-semibold text-white/60 uppercase tracking-wide mb-1.5">First Name *</span>
                  <input required value={lenderForm.first_name} onChange={e => setLenderForm(p => ({ ...p, first_name: e.target.value }))}
                    className="block w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/30 transition-all"
                    placeholder="Jane" data-testid="lender-first-name" /></label>
                <label className="block"><span className="block text-xs font-semibold text-white/60 uppercase tracking-wide mb-1.5">Last Name *</span>
                  <input required value={lenderForm.last_name} onChange={e => setLenderForm(p => ({ ...p, last_name: e.target.value }))}
                    className="block w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/30 transition-all"
                    placeholder="Smith" data-testid="lender-last-name" /></label>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <label className="block"><span className="block text-xs font-semibold text-white/60 uppercase tracking-wide mb-1.5">Email *</span>
                  <input required type="email" value={lenderForm.email} onChange={e => setLenderForm(p => ({ ...p, email: e.target.value }))}
                    className="block w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/30 transition-all"
                    placeholder="jane@lendco.com" data-testid="lender-email" /></label>
                <label className="block"><span className="block text-xs font-semibold text-white/60 uppercase tracking-wide mb-1.5">Phone *</span>
                  <input required value={lenderForm.phone} onChange={e => setLenderForm(p => ({ ...p, phone: e.target.value }))}
                    className="block w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/30 transition-all"
                    placeholder="(832) 555-1234" data-testid="lender-phone" /></label>
              </div>
              <label className="block"><span className="block text-xs font-semibold text-white/60 uppercase tracking-wide mb-1.5">Company Name *</span>
                <input required value={lenderForm.company_name} onChange={e => setLenderForm(p => ({ ...p, company_name: e.target.value }))}
                  className="block w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/30 transition-all"
                  placeholder="Capital Lending LLC" data-testid="lender-company" /></label>
              <div className="grid grid-cols-2 gap-4">
                <label className="block"><span className="block text-xs font-semibold text-white/60 uppercase tracking-wide mb-1.5">City *</span>
                  <input required value={lenderForm.city} onChange={e => setLenderForm(p => ({ ...p, city: e.target.value }))}
                    className="block w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/30 transition-all"
                    placeholder="Houston" data-testid="lender-city" /></label>
                <label className="block"><span className="block text-xs font-semibold text-white/60 uppercase tracking-wide mb-1.5">State</span>
                  <input value={lenderForm.state} onChange={e => setLenderForm(p => ({ ...p, state: e.target.value }))}
                    className="block w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/30 transition-all"
                    placeholder="TX" data-testid="lender-state" /></label>
              </div>
              <label className="block"><span className="block text-xs font-semibold text-white/60 uppercase tracking-wide mb-1.5">Capital Available to Deploy *</span>
                <select required value={lenderForm.capital_available} onChange={e => setLenderForm(p => ({ ...p, capital_available: e.target.value }))}
                  className="block w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white appearance-none cursor-pointer focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/30 transition-all"
                  data-testid="lender-capital">
                  <option value="" className="text-[#0A0A0A]">Select range</option>
                  {CAPITAL_RANGES.map(r => <option key={r} value={r} className="text-[#0A0A0A]">{r}</option>)}
                </select></label>

              <div>
                <span className="block text-xs font-semibold text-white/60 uppercase tracking-wide mb-2">Preferred Loan Types *</span>
                <div className="flex flex-wrap gap-2" data-testid="lender-loan-types">
                  {LOAN_TYPES.map(lt => {
                    const selected = lenderForm.preferred_loan_types.includes(lt);
                    return (
                      <button key={lt} type="button" onClick={() => toggleLoanType(lt)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          selected ? "bg-emerald-500 text-white border border-emerald-400" : "bg-white/10 text-white/60 border border-white/15 hover:bg-white/15"
                        }`} data-testid={`lender-lt-${lt.toLowerCase().replace(/\s/g, '-')}`}>
                        {lt}
                      </button>
                    );
                  })}
                </div>
              </div>

              <label className="block"><span className="block text-xs font-semibold text-white/60 uppercase tracking-wide mb-1.5">Typical Loan Size *</span>
                <select required value={lenderForm.typical_loan_size} onChange={e => setLenderForm(p => ({ ...p, typical_loan_size: e.target.value }))}
                  className="block w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white appearance-none cursor-pointer focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/30 transition-all"
                  data-testid="lender-loan-size">
                  <option value="" className="text-[#0A0A0A]">Select range</option>
                  {DEAL_SIZES.map(s => <option key={s} value={s} className="text-[#0A0A0A]">{s}</option>)}
                </select></label>

              <label className="block"><span className="block text-xs font-semibold text-white/60 uppercase tracking-wide mb-1.5">How did you hear about us?</span>
                <input value={lenderForm.heard_about_us} onChange={e => setLenderForm(p => ({ ...p, heard_about_us: e.target.value }))}
                  className="block w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/30 transition-all"
                  placeholder="Referral, LinkedIn, etc." data-testid="lender-referral" /></label>

              {lenderError && (
                <div className="rounded-xl border border-red-400/30 bg-red-900/30 p-3 text-sm text-red-300 flex items-center gap-2" data-testid="lender-error">
                  <AlertCircle className="h-4 w-4 shrink-0" />{lenderError}
                </div>
              )}

              <button type="submit" disabled={lenderLoading || lenderForm.preferred_loan_types.length === 0}
                className="w-full py-3.5 rounded-xl bg-[#E5C98B] text-[#0A0A0A] text-sm font-bold hover:bg-[#d4b574] transition-colors disabled:opacity-50 shadow-lg"
                data-testid="lender-submit-btn">
                {lenderLoading ? <span className="flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Submitting...</span> : "Submit Lender Application"}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ═══ SECTION 8: URGENCY FOOTER ═══ */}
      <section className="bg-[#022c22] border-t border-emerald-700/30" data-testid="urgency-footer">
        <div className="mx-auto max-w-3xl px-4 py-10 text-center">
          <Lock className="h-6 w-6 text-[#E5C98B] mx-auto mb-3" />
          <p className="text-base sm:text-lg font-semibold text-white">
            This program is limited to 15 total founding members.
          </p>
          <p className="mt-2 text-sm text-white/50">
            Once spots are filled this page will close.
          </p>
        </div>
      </section>
    </div>
  );
}
